<template>
  <d-container
    fluid
    class="main-content-container px-0 px-md-2 px-lg-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        sm="6"
        md="6"
        lg="6"
        class="text-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Zamówienie</span>
        <h3 class="page-title">
          <template v-if="isEdit">Edycja zamówienia</template>
          <template v-else>Dodawanie zamówienia</template>
        </h3>
      </d-col>
      <d-col
        sm="6"
        md="6"
        lg="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/zamowienia')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>
        <d-button
          :disabled="!allDataLoaded || isReadOnly"
          theme="success"
          @click.prevent="validateForm">
          Zapisz zmiany
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <div
          v-if="!allDataLoaded || submitInProgress || !customerForDuplicateLoaded"
          class="overlay-loading">
          <div class="loading-spinner pb-3 pt-3 text-center">
            <span class="loading-spinner-content text-primary">
              <template v-if="submitInProgress">
                Trwa zapisywanie zamówienia&hellip;
              </template>
              <template v-else>
                Trwa ładowanie danych&hellip;
              </template>
            </span>
          </div>
        </div>
        <validation-observer
          ref="order-form"
          tag="form">
          <d-tabs
            card
            :nav-class="allDataLoaded ? validationResults : ''"
            :class="{'is-loading': !allDataLoaded || submitInProgress || !customerForDuplicateLoaded}">
            <!-- Dane podstawowe -->
            <d-tab
              title="Dane podstawowe"
              active
              class="px-3 px-md-4">
              <div class="d-flex border-bottom mb-3 align-items-center flex-wrap flex-lg-nowrap">
                <h4 class="pb-3">Dane podstawowe</h4>
                <div
                  class="form-group mt-0 d-flex mr-auto ml-md-auto mr-md-0 align-items-center flex-wrap flex-sm-nowrap"
                  style="width: 510px;" >
                  <template v-if="isEdit">
                    <label
                      for="status"
                      class="mr-1 mb-0 text-nowrap mb-2 mb-sm-0">
                      Status zamówienia:
                    </label>
                    <order-status-select
                      ref="status-select"
                      class="pl-0 pl-sm-2"
                      :disabled="isStatusSelectBlocked"/>
                  </template>
                  <template v-else>
                    <d-checkbox
                      toggle
                      toggle-small
                      class="mr-0 ml-auto"
                      inline
                      :disabled="isStatusSelectBlocked"
                      v-model="saveWithInPreparationStatus"
                      @change="showStatusChangeWarning">
                      Przy zapisie ustaw status "Wysłane do realizacji"
                    </d-checkbox>
                  </template>
                </div>
              </div>

              <d-alert
                v-if="isStatusSelectBlocked"
                theme="danger"
                show>
                Z powodu zadłużenia klienta zmina satusu została zablokowana.
              </d-alert>

              <d-row>
                <d-col lg="6">
                  <div class="form-group">
                    <label for="client">
                      Klient:
                    </label>
                    <template v-if="!isEdit && allDataLoaded && (!isDuplicate || isDuplicate && data.customer_id)">
                      <order-customer-select
                        ref="customer-select"
                        :showDiscount=true
                        :allCategories="allCategories"
                        :allManufacturers="allManufacturers"
                        :allProducts="allAvailableProducts"
                        :customerID="data.customer_id"
                        :addressID="data.address_id"
                        :paymentID="data.payment_id" />
                    </template>
                    <template v-else-if="isEdit && allDataLoaded && customer">
                      <order-info-customer
                        :selectedCustomer="customer"
                        :allCategories="allCategories"
                        :allManufacturers="allManufacturers"
                        :allProducts="allAvailableProducts" />
                    </template>
                    <template v-else-if="isCustomerNotExisitng">
                      <span class="text-danger d-block">Klient przypisany do zamówienia nie został znaleziony w bazie danych.</span>
                    </template>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col lg="6">
                  <div class="form-group">
                    <label for="address">
                      Adres dostawy:
                    </label>
                    <order-address-select
                      v-if="!isReadOnly && allDataLoaded"
                      :disabled="!isEdit && (!$refs['customer-select'] || !$refs['customer-select'].customer_id)"
                      ref="address-select" />
                    <order-info-address
                      v-if="isReadOnly"
                      :addressName="data.address_name"
                      :addressSurname="data.address_surname"
                      :addressStreet="data.address_street"
                      :addressBuildingNumber="data.address_building_number"
                      :addressHouseNumber="data.address_house_number"
                      :addressPostCode="data.address_post_code"
                      :addressCity="data.address_city" />
                  <d-link
                    v-if="!isCustomerNotExisitng"
                    class="mt-2"
                    theme="success"
                    style="display: block; font-size: 14px;"
                    :disabled="submitInProgress || (!this.data.customer_id && (!$refs['customer-select'] || !$refs['customer-select'].customer_id))"
                    @click.prevent="showAddDeliveryAddress">
                    <i
                      class="material-icons"
                      style="font-size: 14px;">
                      add
                    </i>
                    Dodaj nowy adres
                  </d-link>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col lg="6">
                  <div class="form-group">
                    <label for="payment-method">
                      Metoda Płatności:
                    </label>
                    <order-payment-select
                      v-if="!isReadOnly && allDataLoaded"
                      :disabled="!isEdit && (!$refs['customer-select'] || !$refs['customer-select'].customer_id)"
                      ref="payment-select" />
                    <order-info-payment
                      v-if="isReadOnly"
                      :paymentMethod="data.payment_name"/>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col lg="6">
                  <div class="form-group">
                    <label for="shipping">
                      Metoda dostawy:
                    </label>
                    <order-shipping-select
                      v-if="!isReadOnly && allDataLoaded"
                      ref="shipping-select" />
                    <order-info-shipping
                      v-if="isReadOnly"
                      :shipmentName="data.shipment_name"
                      :shipmentPrice="data.shipment_price" />
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col lg="6">
                  <div class="form-group">
                    <label for="shipment-date">
                      Data wysyłki:
                    </label>

                    <validation-provider
                      name="shipment-date"
                      v-slot="{ errors }">
                      <date-range-picker
                        v-if="shipmentDatePickerVisible"
                        ref="picker"
                        :locale-data="{
                          'firstDay': 1,
                          'format': 'DD-MM-YYYY',
                          'daysOfWeek': ['Ndz', 'Pon', 'Wto', 'Śro', 'Czw', 'Ptk', 'Sob'],
                          'monthNames': ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
                          'applyLabel': 'OK',
                          'cancelLabel': 'Anuluj'
                        }"
                        :class="{
                          'mr-1 mb-2 w-100': true,
                          'form-control-disabled': isReadOnly
                        }"
                        :disabled="isReadOnly"
                        :singleDatePicker="true"
                        :timePicker="false"
                        :showWeekNumbers="false"
                        :showDropdowns="false"
                        v-model="shipmentDate"
                        :ranges="false"
                        :linkedCalendars="true"
                        :date-format="dateFormat">
                        <template v-slot:input="picker">
                          {{ prepareDateString(picker.startDate) }}
                        </template>
                      </date-range-picker>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                    <p
                      v-if="showAlertProductsNotReserved"
                      class="text-warning mb-0">
                      Uwaga! Do momentu wysłania zamówienia system nie blokuje produktów na magazynie.
                    </p>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col lg="6">
                  <div class="form-group">
                    <label for="description">
                      Uwagi do zamówienia:
                    </label>

                    <validation-provider
                      name="description"
                      v-slot="{ errors }">
                      <d-textarea
                        :disabled="isReadOnly"
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.description"
                        :value.prop="data.description"
                        :rows="6"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-alert
                v-if="loadError"
                show
                theme="warning">
                Wczytywanie danych nie powiodło się.
                <a
                  href="javascript:window.location.reload();"
                  class="alert-link">
                  Odśwież stronę
                </a>
                aby spróbować ponownie.
              </d-alert>
            </d-tab>
            <!-- Zamówienie -->
            <d-tab
              title="Zamówienie"
              class="px-3 px-md-4">
              <h4 class="pb-3 mb-0 border-bottom d-flex flex-sm-wrap">
                Zamówienie

                <d-button
                  :disabled="isReadOnly || !customerID || !pricesRecalculated"
                  theme="success"
                  class="ml-auto"
                  @click.prevent="$refs['order-creator'].addProduct()">
                  Dodaj produkt
                </d-button>
              </h4>

              <order-creator
                ref="order-creator"
                :disabled="isReadOnly"
                :addBtnDisabled="isReadOnly || !customerID"
                :isEdit="isEdit"
                :allProducts="allAvailableProducts"
                :orderForm="$refs['order-form']"
                :isCustomerNotExisitng="isCustomerNotExisitng" />

              <div
                v-if="!allDataLoaded"
                class="pb-3 pt-3 text-center">
                Trwa ładowanie danych&hellip;
              </div>

              <d-alert
                v-if="loadError"
                show
                theme="warning">
                Wczytywanie danych nie powiodło się.
                <a
                  href="javascript:window.location.reload();"
                  class="alert-link">
                  Odśwież stronę
                </a>
                aby spróbować ponownie.
              </d-alert>
            </d-tab>
          </d-tabs>
        </validation-observer>
      </d-card>
    </d-form>

    <client-delivery-address-popup />
  </d-container>
</template>

<script>
import Vue from 'vue';
import FormUtils from './../../utils/FormUtils.js';
import OrderCreator from './order/OrderCreator.vue';
import OrderAddressSelect from './order/OrderAddressSelect.vue';
import OrderCustomerSelect from './order/OrderCustomerSelect.vue';
import OrderInfoAddress from './order/OrderInfoAddress.vue';
import OrderInfoCustomer from './order/OrderInfoCustomer.vue';
import OrderInfoShipping from './order/OrderInfoShipping.vue';
import OrderShippingSelect from './order/OrderShippingSelect.vue';
import OrderStatusSelect from './order/OrderStatusSelect.vue';
import OrderPaymentSelect from './order/OrderPaymentSelect.vue';
import OrderInfoPayment from './order/OrderInfoPayment.vue';
import OrderStatusPhases from './../../data/order-phases.js';
import ClientDeliveryAddressPopup from './../popups/ClientDeliveryAddressPopup.vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import holidays from './../../data/holidays.js';

export default {
  name: 'order-form',
  components: {
    'order-creator': OrderCreator,
    'order-address-select': OrderAddressSelect,
    'order-customer-select': OrderCustomerSelect,
    'order-info-address': OrderInfoAddress,
    'order-info-customer': OrderInfoCustomer,
    'order-info-shipping': OrderInfoShipping,
    'order-shipping-select': OrderShippingSelect,
    'order-status-select': OrderStatusSelect,
    'order-payment-select': OrderPaymentSelect,
    'order-info-payment': OrderInfoPayment,
    'client-delivery-address-popup': ClientDeliveryAddressPopup,
    'date-range-picker': DateRangePicker
  },
  computed: {
    customerForDuplicateLoaded () {
      if (!this.isDuplicate) {
        return true;
      }
      return this.customerForDuplicateInitialized;
    },
    allDataLoaded () {
      return this.loaded && this.loadedProducts && this.loadedCategories && this.loadedManufacturers;
    },
    isCustomerNotExisitng () {
      if (!this.isEdit || !this.allDataLoaded) {
        return false;
      }

      if (!this.customer) {
        return true;
      }

      return false;
    },
    isReadOnly () {
      if (this.isCustomerNotExisitng) {
        return true;
      }
      return OrderStatusPhases.readOnly.indexOf(this.data.status) > -1;
    },
    isEdit () {
      return !!this.$route.params.id;
    },
    isDuplicate () {
      return !!this.$route.params.existingOrderID;
    },
    isAdmin () {
      return this.$store.getters['getUserType'] === 'ADMIN';
    },
    isStatusSelectBlocked () {
      return this.isCustomerInDebt && !this.isAdmin;
    },
    validationResults() {
      if (!this.allDataLoaded || !this.$refs['order-form'] || !this.isValidated) {
        return '';
      }

      let rawErrors = this.$refs['order-form'].errors;
      let errorKeys = Object.keys(rawErrors);
      let errors = [];

      for (let i = 0; i < errorKeys.length; i++) {
        if (rawErrors[errorKeys[i]].length) {
          if (errorKeys[i].indexOf('product-') > -1) {
            errors.push('order-creator');
          } else {
            errors.push(errorKeys[i]);
          }
        }
      }

      let errorTabs = [];
      let tab1Fields = [
        'status',
        'customer',
        'address',
        'shipment',
        'payment-method'
      ];

      for (let i = 0; i < tab1Fields.length; i++) {
        if (errors.indexOf(tab1Fields[i]) > -1) {
          errorTabs.push('error-1');
          break;
        }
      }

      if (errors.indexOf('order-creator') > -1 || !this.$refs['order-creator'].products.length) {
        errorTabs.push('error-2');
      }

      return errorTabs.join(' ');
    },
    showAlertProductsNotReserved () {
      if (!this.shipmentDatePickerVisible || !this.shipmentDate || !this.shipmentDate.startDate) {
        return false;
      }

      let tomorrowDate = new Date();
      tomorrowDate.setDate(tomorrowDate.getDate() + 1);

      if (this.shipmentDate.startDate.getFullYear() > tomorrowDate.getFullYear()) {
        return true;
      }

      if (this.shipmentDate.startDate.getMonth() > tomorrowDate.getMonth()) {
        return true;
      }

      if (this.shipmentDate.startDate.getDate() > tomorrowDate.getDate()) {
        return true;
      }

      return false;
    }
  },
  watch: {
    'shipmentDate.startDate': function (newValue) {
      if (newValue === null) {
        this.data.delivery_date = null;
      }

      this.data.delivery_date = this.prepareDateString(newValue);
    },
    isStatusSelectBlocked (newValue) {
      if (newValue) {
        Vue.set(this, 'saveWithInPreparationStatus', false);
      }
    }
  },
  data() {
    return {
      data: {
        id: null,
        shipment_id: null,
        customer_id: null,
        address_id: null,
        status: null,
        products: [],
        customer_discount: 0,
        customer_name: '',
        customer_surname: '',
        address_name: '',
        address_surname: '',
        address_street: '',
        address_building_number: '',
        address_house_number: '',
        address_post_code: '',
        address_city: '',
        shipment_name: '',
        shipment_price: '',
        description: '',
        delivery_date: null,
        payment_id: null
      },
      allAvailableProducts: [],
      allManufacturers: [],
      allCategories: [],
      loadedManufacturers: false,
      loadedCategories: false,
      loadedProducts: false,
      loaded: false,
      loadError: false,
      unwatchLoaded: null,
      shipmentDatePickerVisible: false,
      shipmentDate: {
        startDate: null
      },
      customerID: null,
      customerForDuplicateInitialized: false,
      submitInProgress: false,
      customer: null,
      isValidated: false,
      saveWithInPreparationStatus: false,
      pricesRecalculated: true,
      isCustomerInDebt: false
    };
  },
  mounted() {
    this.$bus.$on('order-client-discounts-updated', this.setPricesRecalculated);
    this.$bus.$on('order-client-discounts-loading', this.setPricesRecalculated);
    if (this.isEdit || this.isDuplicate) {
      let overrideID = null;
      if (this.isDuplicate) {
        overrideID = this.$route.params.existingOrderID;
      }

      this.loadItemData(overrideID);

      this.unwatchLoaded = this.$watch('allDataLoaded', newValue => {
        if (!newValue) {
          return;
        }

        Vue.nextTick(() => {
          if (!this.isReadOnly) {
            this.$refs['shipping-select'].setShipmentID(this.data.shipment_id);

            if (!this.isDuplicate) {
              this.$bus.$emit('order-update-payment-list', this.customer.payments, this.data.payment_id);
              this.$refs['address-select'].loadCustomerAddressesData(this.customer.addresses, this.data.address_id);
            }
          } else {
            this.$bus.$emit('order-update-shipment-cost', this.data.shipment_price);
          }

          if (!this.isDuplicate) {
            this.$bus.$emit('order-update-client-discounts', this.data.customer_id);
            this.$refs['status-select'].setStatus(this.data.status, this.data.id);
          }

          this.customerID = this.data.customer_id;
          this.unwatchLoaded();
        });
      });
    } else {
      this.loaded = true;
      this.shipmentDatePickerVisible = true;
      this.loadAdditionalData();
    }

    this.$bus.$on('order-update-selected-client', this.setCurrentCustomer);
    this.$bus.$on('order-status-changed', this.changeOrderStatus);
    this.$bus.$on('order-data-loaded', this.orderDataLoaded);
    this.$bus.$on('validate-order-form', this.validateForm);
  },
  methods: {
    loadItemData(orderID = null) {
      FormUtils.loadItemData(this, {
        overrideID: orderID,
        method: 'get',
        endpointBase: '/api/orders/item/',
        instanceFields: [
          'id',
          'shipment_id',
          'customer_id',
          'address_id',
          'shipment_id',
          'status',
          'products',
          'customer_name',
          'customer_surname',
          'customer_discount',
          'address_name',
          'address_surname',
          'address_street',
          'address_building_number',
          'address_house_number',
          'address_post_code',
          'address_city',
          'shipment_name',
          'shipment_price',
          'description',
          'delivery_date',
          'payment_id',
          'payment_name'
        ],
        loadedKey: 'loaded',
        successAction: (response) => {
          Vue.nextTick(() => {
            if (this.isDuplicate) {
              Vue.set(this.data, 'id', null);
              Vue.set(this.data, 'delivery_date', null);
              Vue.set(this.data, 'status', null);
            } else if (!this.isDuplicate && response.delivery_date !== null) {
              Vue.set(this.shipmentDate, 'startDate', new Date(response.delivery_date));
            }
            this.loadAdditionalData();
            this.shipmentDatePickerVisible = true;
          });
        }
      });
    },
    loadAdditionalData () {
      let productsEndpointUrl = this.isEdit ? '/api/products/all-items?customer_id=' + this.data.customer_id : '/api/products/all-items';

      FormUtils.loadAdditionalData(this, {
        endpoint: productsEndpointUrl,
        method: 'get',
        outputKey: 'allAvailableProducts',
        loadedKey: 'loadedProducts',
        errorKey: 'loadError',
        successAction: () => {
          Vue.nextTick(() => {
            this.$refs['order-creator'].setUpProducts(this.data.products);
            this.$bus.$emit('order-products-all-loaded');
          });
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/manufacturers/items',
        method: 'get',
        outputKey: 'allManufacturers',
        loadedKey: 'loadedManufacturers',
        errorKey: 'loadError',
        params: {
          pagination: 0,
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/categories/items',
        method: 'get',
        outputKey: 'allCategories',
        loadedKey: 'loadedCategories',
        errorKey: 'loadError',
        params: {
          pagination: 0,
        }
      });

      if (this.isEdit) {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: '/api/contacts/item/' + this.data.customer_id,
          outputKey: 'customer',
          errorKey: 'loadError',
          wrapResponseIntoArray: false,
          successAction: () => {
            // for no longer exisitng client assume there is no debt
            if (this.customer) {
              Vue.set(this, 'isCustomerInDebt', this.customer.debt_amount > 0);
            }
          }
        });
      }
    },
    validateForm() {
      this.isValidated = true;
      FormUtils.validate(this.$refs['order-form'], this.submitForm);
    },
    submitForm() {
      if (this.validationResults !== '') {
        return;
      }

      this.submitInProgress = true
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.isEdit
        ? '/api/orders/update/' + this.id
        : '/api/orders/store';

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        loadedKey: 'loaded',
        successTitle: 'Zapisano zamówienie',
        successText: 'Wybrane zamówienie zostało zapisane',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja danych zamówienia nie powiodła się. Spróbuj ponownie.',
        successAction: (response) => {
          if (!this.saveWithInPreparationStatus) {
            this.$router.push('/zamowienia');
            this.submitInProgress = false;
            return;
          }

          this.setInPreparationStatus(response.item.id);
        },
        failAction: () => {
          this.submitInProgress = false;
        },
      });
    },
    prepareFormDataToSend() {
      let formDataToSend = {
        address_id: this.$refs['address-select'].address_id,
        shipment_id: this.$refs['shipping-select'].shipment_id,
        description: this.data.description,
        delivery_date: this.data.delivery_date,
        payment_id: this.$refs['payment-select'].payment_id
      };

      formDataToSend.products = this.$refs['order-creator'].getProducts();

      if (!this.isEdit) {
        formDataToSend.customer_id = this.$refs['customer-select'].customer_id;
      }

      return formDataToSend;
    },
    changeOrderStatus (newStatus) {
      Vue.set(this.data, 'status', newStatus);
    },
    orderDataLoaded (dataType) {
      if (dataType === 'shipping' && this.data.shipment_id !== null) {
        this.$refs['shipping-select'].setShipmentID(this.data.shipment_id);
      }
    },
    showAddDeliveryAddress() {
      if (!this.isEdit) {
        this.$bus.$emit('client-delivery-address-popup-show', this.$refs['customer-select'].customer_id);
      } else {
        this.$bus.$emit('client-delivery-address-popup-show', this.data.customer_id);
      }
    },
    dateFormat (classes, date) {
      if (!classes.disabled) {
        classes.disabled = this.checkIfDayNotForShipment(date);
      }
      return classes;
    },
    checkIfDayNotForShipment (date) {
      let currentDate = new Date();
      let isNotForShipment = false;

      if (date < currentDate) {
        isNotForShipment = true;
      } else if (holidays.moveable.indexOf(date.toLocaleDateString()) >= 0) {
        isNotForShipment = true;
      } else if (holidays.fixed.indexOf(date.toLocaleDateString().split('.').splice(0, 2).join('.')) >= 0) {
        isNotForShipment = true;
      } else if (date.getDay() === 0 || date.getDay() === 6) {
        isNotForShipment = true;
      } else if (date.getFullYear() === currentDate.getFullYear() && date.getMonth() === currentDate.getMonth() && date.getDate() <= currentDate.getDate()) {
        isNotForShipment = true;
      }

      return isNotForShipment;
    },
    prepareDateString (input) {
      if (input === null) {
        return 'Wybierz datę';
      }
      input = new Date(input);

      return [
        input.getFullYear(),
        input.getMonth() + 1,
        input.getDate()
      ]
        .map(n => n < 10 ? '0' + n : n)
        .join('-');
    },
    setCurrentCustomer ({ customerID, isInDebt = false }) {
      Vue.set(this, 'isCustomerInDebt', isInDebt);
      this.customerID = customerID;
      if ((this.isDuplicate) && !this.customerForDuplicateInitialized) {
        this.customerForDuplicateInitialized = true;
      }
    },
    showStatusChangeWarning (newValue) {
      if (!newValue) {
        return;
      }

      Vue.swal({
        title: 'Zmiana statusu',
        text: 'Czy na pewno chcesz przy zapisie nadać zamówieniu status "Wysłane do realizacji"? Po zapisie nie będzie możliwa już edycja tego zamówienia i zamówienie zostanie wysłane do realizacji',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Tak, zmień status',
        cancelButtonText: 'Anuluj'
      }).then((result) => {
        if (!result.value) {
          Vue.set(this, 'saveWithInPreparationStatus', false);
        }
      });
    },
    setInPreparationStatus (orderID) {
      FormUtils.submitInBackground(this, {
        endpoint: '/api/orders/add-status/' + orderID,
        params: { status: 'IN_PREPARATION' },
        errorTitle: 'Wystąpił błąd',
        errorText: 'Zmiana statusu zamówienia nie powiodła się.',
        successAction: () => {
          this.$router.push('/zamowienia');
          this.submitInProgress = false;
        },
        failAction: () => {
          this.submitInProgress = false;
        }
      });
    },
    setPricesRecalculated (value) {
      Vue.set(this, 'pricesRecalculated', value);
    }
  },
  beforeDestroy() {
    this.$bus.$off('order-client-discounts-updated', this.setPricesRecalculated);
    this.$bus.$off('order-client-discounts-loading', this.setPricesRecalculated);
    this.$bus.$off('order-status-changed', this.changeOrderStatus);
    this.$bus.$off('order-data-loaded', this.orderDataLoaded);
    this.$bus.$off('order-update-selected-client', this.setCurrentCustomer);
    this.$bus.$off('validate-order-form', this.validateForm);
  }
};
</script>

<style lang="scss" scoped>
a.disabled {
  cursor: not-allowed;
}
</style>
